/**
 * `Idempotency-Key` values (design/07 §1): one UUID v7 per mutation, made on the tablet.
 *
 * A retry of the *same* action (a lost answer, a double tap) must reuse its key — so screens that
 * can be retried (お会計, offline queue) create the key once and keep it; the API layer adds a fresh
 * one only to mutations sent without a key. v7 = time-ordered (sortable in the offline queue).
 */

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** RFC 9562 UUID version 7: 48-bit Unix ms timestamp, version 7, variant 10, 74 random bits. */
export function uuidv7(
  now: number = Date.now(),
  random: Uint8Array = crypto.getRandomValues(new Uint8Array(10)),
): string {
  if (!Number.isInteger(now) || now < 0) {
    throw new RangeError("timestamp must be a non-negative integer (ms)");
  }
  if (random.length < 10) {
    throw new RangeError("uuidv7 needs 10 random bytes");
  }
  const bytes = new Uint8Array(16);
  let ts = now;
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = ts % 256;
    ts = Math.floor(ts / 256);
  }
  bytes.set(random.subarray(0, 10), 6);
  bytes[6] = 0x70 | ((bytes[6] ?? 0) & 0x0f);
  bytes[8] = 0x80 | ((bytes[8] ?? 0) & 0x3f);
  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/** Methods that change something — every one carries an `Idempotency-Key`. */
export const MUTATING_METHODS: ReadonlySet<string> = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Paths whose POST is a calculation (no key: nothing to replay — T07-24 お会計 preview,
 * T07-27 返品 preview). */
export function isReadOnlyPost(path: string): boolean {
  return path.endsWith("/tender-preview") || path.endsWith("/returns/preview");
}

export function newIdempotencyKey(): string {
  return uuidv7();
}
